<template>
  <dialog
    class="AchievementModalView"
    data-theme="light"
    data-testid="achievementModal"
    open
  >
    <div
      class="AchievementModalView__backdrop"
      @click="$emit('close')"
    />
    <div
      class="AchievementModalView__window"
    >
      <div class="AchievementModalView__content">
        <h3 class="AchievementModalView__title">
          Achievement unlocked!
        </h3>
        <div class="AchievementModalView__achievement">
          <Basketball class="AchievementModalView__achievementIcon" />
          <h4
            class="AchievementModalView__achievementName"
            data-testid="achievementModalTitle"
          >
            {{ modelValue.name }}
          </h4>
          <p
            class="AchievementModalView__achievementDescription"
            data-testid="achievementModalDescription"
          >
            {{ modelValue.description }}
          </p>
        </div>
      </div>
      <FormButton
        @click="handleViewAchievements"
      >
        View achievements
      </FormButton>
    </div>
  </dialog>
</template>

<script lang="ts" setup>
import Basketball from "@/components/Basketball.vue";
import FormButton from "@/components/FormButton.vue";
import { RouteName } from "@/lib/router/routes";
import { Achievement } from "@shared/lib/database/query.types";
import { useRouter } from "vue-router";

const modelValue = defineModel<Achievement>({
  required: true,
});

const emit = defineEmits<{
  close: [],
}>();

const router = useRouter();

async function handleViewAchievements () {
  emit("close");

  router.push({
    name: RouteName.achievements,
  });
}
</script>

<style lang="scss" scoped>
.AchievementModalView {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
  isolation: isolate;

  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  padding: 16px;

  transition: none 350ms;
}

.AchievementModalView__backdrop {
  position: absolute;
  inset: 0;
  z-index: -1;

  background-color: var(--themeColorBackdrop);
  backdrop-filter: blur(6px);

  cursor: pointer;

  transition: opacity 350ms var(--easeOutBack);

  .v-leave-active & {
    transition: 350ms;
    transition-timing-function: initial;
  }

  .v-enter-from &,
  .v-leave-to & {
    opacity: 0;
  }
}

.AchievementModalView__window {
  display: grid;
  gap: 32px;
  width: 100%;
  padding: 24px 20px;

  border-radius: 16px;
  background-color: var(--themeColorNeutral0);

  font-stretch: var(--fontWidthExtended);
  text-align: center;
  line-height: 1.5;

  content: "";

  .v-enter-active & {
    transition: 350ms var(--easeOutBack);
    transition-property: opacity, transform;
  }

  .v-leave-active & {
    transition: 350ms;
    transition-property: opacity;
  }

  .v-enter-from & {
    transform: scale(0.8);
    opacity: 0;
  }

  .v-leave-to & {
    opacity: 0;
  }
}

.AchievementModalView__content {
  display: grid;
  gap: 24px;
}

.AchievementModalView__title {
  font-size: 18px;
  font-weight: var(--fontWeightMedium);
}

.AchievementModalView__achievement {
  display: grid;
  justify-items: center;
}

.AchievementModalView__achievementIcon {
  --iconColor: #cd7f32;

  width: 72px;
  height: 72px;
  margin-bottom: 12px;
}

.AchievementModalView__achievementName {
  font-size: 24px;
  font-weight: var(--fontWeightMedium);
}

.AchievementModalView__achievementDescription {
  font-size: 18px;
}
</style>
